import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import bgimg from "../../assets/loginbg.png";
import leftbgimg from "../../assets/leftbg.jpg";
import SellerIndividualCreateProfileForm from "./SellerIndividualCreateProfileForm";
import SellerCorporateCreateProfileForm from "./SellerCorporateCreateProfileForm";
import BuyerRegistrationForm from "./common/BuyerRegistrationForm";
import SellerRegistrationForm from "./common/SellerRegistrationForm";
import BuyerIndividualCreateProfileForm from "./BuyerIndividualCreateProfileForm";
import BuyerCorporateCreateProfileForm from "./BuyerCorporateCreateProfileForm";

import api from "../../lib/api.ts";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";

const MainRegistrationForm = ({
  tabs = ["Seller", "Buyer"],
  defaultTab = "Buyer",
  removeBackground = false,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [accountType, setAccountType] = useState("");
  const [accountTypes, setAccountTypes] = useState([]);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [formData, setFormData] = useState(null);
  const [sellerTypeId, setSellerTypeId] = useState(null);
  const [isUniquenessValidated, setIsUniquenessValidated] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  // Fetch account types from API
  useEffect(() => {
    const fetchAccountTypes = async () => {
      try {
        const response = await api.get(`/users/types`, { timeout: 3000 });

        const data = response.data;
        setAccountTypes(data);

        if (data.length > 0) {
          // Find Individual account type first, fallback to first item if not found
          const individualType = data.find(
            (type) => type.name === "Individual"
          );
          if (individualType) {
            setAccountType("Individual");
            setSellerTypeId(individualType.id);
          } else {
            setAccountType(data[0].name);
            setSellerTypeId(data[0].id);
          }
        }
      } catch (error) {
        console.error("Error fetching account types:", error.message);

        // fallback
        const fallbackTypes = [
          { id: 1, name: "Individual", slug: "individual" },
          { id: 2, name: "Corporate", slug: "corporate" },
        ];
        setAccountTypes(fallbackTypes);
        setAccountType("Individual");
        setSellerTypeId(1);
      }
    };

    fetchAccountTypes();
  }, []);

  useEffect(() => {
    emailjs.init("AvsKDMWekYQHtsEby");
  }, []);

  const handleInitialSubmit = async (data) => {
    // Check if all required fields are present
    if (
      !data.name ||
      !data.email ||
      !data.password ||
      !data.mobileNo ||
      !accountType
    ) {
      console.error("Missing required fields:", {
        name: !data.name,
        email: !data.email,
        password: !data.password,
        mobileNo: !data.mobileNo,
        accountType: !accountType,
      });
      showError("Please fill in all required fields before proceeding.");
      return;
    }

    // Check email and mobile uniqueness before proceeding
    try {
      setIsLoading(true);

      const checkUniqueResponse = await api.post(`/auth/checkunique`, {
        email: data.email,
        mobileNo: data.mobileNo,
      });

      // If unique, go straight to Complete Profile
      setFormData(data);
      setShowCompleteProfile(true);
      setIsUniquenessValidated(true);
    } catch (error) {
      console.error("Error checking uniqueness:", error);

      // Handle server-side validation errors
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          showError(errorData.message);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedToProfile = () => {
    setShowCompleteProfile(true);
  };

  const createFormData = (data, files = {}) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined && data[key] !== "") {
        formData.append(key, data[key]);
      }
    });
    Object.keys(files).forEach((key) => {
      if (files[key]) {
        formData.append(key, files[key]);
      }
    });
    return formData;
  };

  // Handle complete profile submission
  const handleCompleteProfile = async (profileData) => {
    setIsLoading(true);
    try {
      const selectedAccountType = accountTypes.find(
        (type) => type.name === accountType
      );
      const accountTypeId = selectedAccountType ? selectedAccountType.id : 1;

      // Create separate data structures for buyers and sellers
      let completeData = {};
      let files = {};

      if (activeTab.toLowerCase() === "buyer") {
        // Buyer-specific data structure
        completeData = {
          name: formData.name?.trim(),
          email: formData.email?.trim().toLowerCase(),
          password: formData.password,
          mobileNo: formData.mobileNo?.trim(),
          location: profileData.location?.trim(),
          identificationNo: profileData.identificationNo?.trim(),
          idExprDate: profileData.idExprDate,
          bankName: profileData.bankName?.trim() || "",
          accountHolderName: profileData.accountHolderName?.trim() || "",
          accountNumber: profileData.accountNumber?.trim() || "",
          iban: profileData.iban?.trim() || "",
          userType: "buyer",
          accountType: accountType.toLowerCase(),
          buyerType: accountType.toLowerCase(), // Add buyer type field
          type: accountType.toLowerCase(), // Alternative field name
          userTypeId: accountTypeId, // Add user type ID for buyers
          // Additional variations that backend might expect
          buyer_type: accountType.toLowerCase(),
          buyerTypeId: accountTypeId,
          buyer_type_id: accountTypeId,
        };

        // Add business fields for corporate buyers
        if (accountType.toLowerCase() === "corporate") {
          completeData.businessName = profileData.businessName?.trim();
          completeData.businessRegNo = profileData.businessRegNo?.trim();
          completeData.vatNo = profileData.vatNo?.trim() || "";
          // Alternative field names for business info
          completeData.companyName = profileData.businessName?.trim();
          completeData.companyRegNo = profileData.businessRegNo?.trim();
        }

        // Buyer files
        if (profileData.eIdFrontImage)
          files.eIdFrontImage = profileData.eIdFrontImage;
        if (profileData.eIdBackImage)
          files.eIdBackImage = profileData.eIdBackImage;

        // Corporate buyer specific files
        if (accountType.toLowerCase() === "corporate") {
          if (profileData.tradeLicenseImage)
            files.tradeLicenseImage = profileData.tradeLicenseImage;
          if (profileData.trnCertificateImage)
            files.trnCertificateImage = profileData.trnCertificateImage;
        }
      } else if (activeTab.toLowerCase() === "seller") {
        // Seller-specific data structure
        completeData = {
          name: formData.name?.trim(),
          email: formData.email?.trim().toLowerCase(),
          password: formData.password,
          mobileNo: formData.mobileNo?.trim(),
          location: profileData.location?.trim(),
          identificationNo: profileData.identificationNo?.trim(),
          vatNo: profileData.vatNo?.trim() || "",
          idExprDate: profileData.idExprDate,
          bankName: profileData.bankName?.trim() || "",
          accountHolderName: profileData.accountHolderName?.trim() || "",
          accountNumber: profileData.accountNumber?.trim() || "",
          iban: profileData.iban?.trim() || "",
          businessName: profileData.businessName?.trim(),
          businessRegNo: profileData.businessRegNo?.trim(),
          sellerTypeId: accountTypeId,
          userType: "seller",
          accountType: accountType.toLowerCase(),
        };

        // Seller files
        if (profileData.eIdFrontImage)
          files.eIdFrontImage = profileData.eIdFrontImage;
        if (profileData.eIdBackImage)
          files.eIdBackImage = profileData.eIdBackImage;
        if (profileData.tradeLicenseImage)
          files.tradeLicenseImage = profileData.tradeLicenseImage;
        if (profileData.trnCertificateImage)
          files.trnCertificateImage = profileData.trnCertificateImage;
      }

      // Remove empty or undefined values to prevent validation errors
      Object.keys(completeData).forEach((key) => {
        if (
          completeData[key] === undefined ||
          completeData[key] === null ||
          completeData[key] === ""
        ) {
          delete completeData[key];
        }
      });

      let apiEndpoint = "";
      let registrationFormData;

      if (activeTab.toLowerCase() === "buyer") {
        apiEndpoint = `/auth/registerbuyer`;
        registrationFormData = createFormData(completeData, files);
      } else if (activeTab.toLowerCase() === "seller") {
        apiEndpoint = `/auth/registerseller`;
        registrationFormData = createFormData(completeData, files);
      }

      const response = await api.post(apiEndpoint, registrationFormData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });

      if (
        response.data.success ||
        response.status === 200 ||
        response.status === 201
      ) {
        // Show success message
        const successMessage =
          activeTab.toLowerCase() === "seller"
            ? "Seller registration successful! Redirecting to dashboard..."
            : "Buyer registration successful! Redirecting to dashboard...";

        showSuccess(successMessage);

        // Automatically log the user in after successful registration
        try {
          const userData = {
            id: response.data.user?.id || response.data.id,
            email: formData.email,
            name: formData.name,
            userType: activeTab.toLowerCase(),
            username: formData.name,
          };

          const token = response.data.token || response.data.accessToken;
          if (token) {
            login(token, userData);
          }
        } catch (error) {
          console.error("Auto-login failed:", error);
          console.log("Auto-login failed:", error);
        }

        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        throw new Error(response.data.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration failed:", error);

      // Enhanced error logging for debugging
      if (error.response) {
        console.error("Error response status:", error.response.status);
        console.error("Error response data:", error.response.data);
        console.error("Error response headers:", error.response.headers);
      }

      let errorMessage = "Registration failed. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.errors) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors;
        if (Array.isArray(validationErrors)) {
          errorMessage = validationErrors.join(", ");
        } else if (typeof validationErrors === "object") {
          errorMessage = Object.values(validationErrors).join(", ");
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.code === "ECONNABORTED") {
        errorMessage =
          "Request timeout. Please check your connection and try again.";
      } else if (error.code === "NETWORK_ERROR") {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      // Show error message with custom alert
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowCompleteProfile(false);
    setFormData(null);
    setIsUniquenessValidated(false);

    if (accountTypes.length > 0) {
      const selectedAccountType = accountTypes.find(
        (type) => type.name === accountType
      );
      setSellerTypeId(selectedAccountType ? selectedAccountType.id : 1);
    }
  };

  const handleGoBack = () => {
    setShowCompleteProfile(false);
    setIsUniquenessValidated(false);
  };

  const shouldSwapSides = activeTab === "Seller";

  const handleAccountTypeChange = (newAccountType) => {
    setAccountType(newAccountType);
    const selectedAccountType = accountTypes.find(
      (type) => type.name === newAccountType
    );
    setSellerTypeId(selectedAccountType ? selectedAccountType.id : 1);
  };

  // Get translated tab labels
  const getTabLabel = (tab) => {
    return translate(`auth.signup.${tab.toLowerCase()}`) || tab;
  };

  const renderCurrentForm = () => {
    if (showCompleteProfile) {
      if (activeTab === "Buyer") {
        return accountType === "Individual" ? (
          <BuyerIndividualCreateProfileForm
            onSubmit={handleCompleteProfile}
            isLoading={isLoading}
            formData={formData}
            onGoBack={handleGoBack}
          />
        ) : (
          <BuyerCorporateCreateProfileForm
            onSubmit={handleCompleteProfile}
            isLoading={isLoading}
            formData={formData}
            onGoBack={handleGoBack}
          />
        );
      } else {
        return accountType === "Individual" ? (
          <SellerIndividualCreateProfileForm
            onSubmit={handleCompleteProfile}
            isLoading={isLoading}
            formData={formData}
            onGoBack={handleGoBack}
          />
        ) : (
          <SellerCorporateCreateProfileForm
            onSubmit={handleCompleteProfile}
            isLoading={isLoading}
            formData={formData}
            onGoBack={handleGoBack}
          />
        );
      }
    } else {
      if (activeTab === "Buyer") {
        return (
          <BuyerRegistrationForm
            onSubmit={handleInitialSubmit}
            isLoading={isLoading}
            accountTypes={accountTypes}
            accountType={accountType}
            setAccountType={handleAccountTypeChange}
            initialValues={formData}
            isUniquenessValidated={isUniquenessValidated}
            onProceed={handleProceedToProfile}
          />
        );
      } else {
        return (
          <SellerRegistrationForm
            onSubmit={handleInitialSubmit}
            isLoading={isLoading}
            accountTypes={accountTypes}
            accountType={accountType}
            setAccountType={handleAccountTypeChange}
            initialValues={formData}
            isUniquenessValidated={isUniquenessValidated}
            onProceed={handleProceedToProfile}
          />
        );
      }
    }
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
      className={`min-h-screen p-2 sm:p-4 flex items-center justify-center ${
        removeBackground ? "" : "bg-cover bg-center"
      }`}
      style={removeBackground ? {} : { backgroundImage: `url(${bgimg})` }}
    >
      <div className='bg-white/20 backdrop-blur-lg w-full max-w-7xl rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl border-2 sm:border-[3px] border-white relative overflow-visible md:overflow-hidden mt-12 sm:mt-16 md:mt-4 mb-4'>
        {/* Tabs */}
        {tabs.length > 1 && !showCompleteProfile && (
          <div
            className={`absolute -top-0 sm:-top-2 md:top-0 ${
              isRTL
                ? "right-1/2 transform translate-x-1/2"
                : "left-1/2 transform -translate-x-1/2"
            } flex ${isRTL ? "flex-row-reverse" : ""} z-50`}
          >
            {(isRTL ? [...tabs].reverse() : tabs).map((tab, index) => {
              const isFirstTab = index === 0;
              const isLastTab = index === tabs.length - 1;
              return (
                <button
                  key={tab}
                  type='button'
                  onClick={() => handleTabChange(tab)}
                  className={`px-3 py-2 text-[13px] leading-none sm:px-5 sm:py-3 sm:text-base md:h-[100px] md:w-[200px] md:text-[32px] font-medium transition-all duration-300 cursor-pointer border-2 select-none whitespace-nowrap ${
                    isRTL
                      ? isFirstTab
                        ? "rounded-r-xl sm:rounded-r-2xl"
                        : isLastTab
                        ? "rounded-l-xl sm:rounded-l-2xl"
                        : ""
                      : isFirstTab
                      ? "rounded-l-xl sm:rounded-l-2xl"
                      : isLastTab
                      ? "rounded-r-xl sm:rounded-r-2xl"
                      : ""
                  } ${
                    activeTab === tab
                      ? "bg-[#E55A2B] text-white shadow-lg border-[#E55A2B]"
                      : "bg-white/90 text-gray-700 border-gray-300 hover:bg-[#E55A2B] hover:text-white hover:border-[#E55A2B] hover:shadow-lg"
                  }`}
                  style={{
                    minHeight: "44px",
                    minWidth: "96px",
                  }}
                >
                  {getTabLabel(tab)}
                </button>
              );
            })}
          </div>
        )}

        <div
          className={`flex flex-col md:flex-row min-h-0 md:min-h-[800px] relative transition-all duration-500 ease-in-out pt-3 sm:pt-6 md:pt-0 ${
            shouldSwapSides ? "md:flex-row-reverse" : ""
          }`}
        >
          {/* Left Side */}
          <div className='md:w-1/2 relative text-white p-4 sm:p-6 md:p-8 lg:p-10 overflow-hidden min-h-[250px] sm:min-h-[300px] md:min-h-auto'>
            <div
              className='absolute inset-0 bg-cover bg-center transition-all duration-500 ease-in-out'
              style={{ backgroundImage: `url(${leftbgimg})` }}
            >
              <div className='absolute inset-0 bg-black opacity-20'></div>
            </div>
            <div className='relative z-10 text-center pt-8 sm:pt-12 md:pt-20 pb-20 sm:pb-24 md:pb-6'>
              <h2 className='text-xl sm:text-2xl md:text-3xl lg:text-[60px] font-semibold mb-3 sm:mb-4'>
                {showCompleteProfile
                  ? translate("auth.signup.completeProfile")
                  : translate("auth.signup.title")}
              </h2>
              <p className='text-xs sm:text-sm md:text-base lg:text-[32px] text-white/90 leading-tight sm:leading-normal'>
                {showCompleteProfile
                  ? translate("auth.signup.additionalDetails")
                  : activeTab === "Buyer"
                  ? translate("auth.signup.buyerSubtitle")
                  : translate("auth.signup.sellerSubtitle")}
              </p>
            </div>
          </div>

          {/* Right Side */}
          <div className='md:w-1/2 bg-white/70 backdrop-blur-md p-4 sm:p-6 md:p-8 flex items-start md:items-center justify-center overflow-y-visible md:overflow-y-auto max-h-none md:max-h-[800px] transition-all duration-500 ease-in-out'>
            {renderCurrentForm()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainRegistrationForm;
